import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { authService } from '../services/authService';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) {
        console.log('❌ No userId provided to profileOperations.load');
        return;
      }
      setProfileLoading(true);
      console.log('🔄 Loading profile for userId:', userId);
      try {
        const { data, error } = await authService?.getUserProfile(userId);
        if (error) {
          console.error('❌ Profile load error:', error);
          // Even on error, set loading to false
          setProfileLoading(false);
          return;
        }
        if (data) {
          console.log('✅ Profile loaded successfully:', data);
          setUserProfile(data);
        } else {
          console.warn('⚠️ No profile data returned');
        }
      } catch (error) {
        console.error('❌ Profile load exception:', error);
      } finally {
        setProfileLoading(false);
        console.log('✅ Profile loading complete');
      }
    },

    clear() {
      console.log('🧹 Clearing profile');
      setUserProfile(null);
      setProfileLoading(false);
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    try {
      supabase?.auth?.getSession()?.then(({ data: { session } }) => {
        authStateHandlers?.onChange(null, session)
      })?.catch((err) => {
        console.error('❌ getSession error:', err);
        setLoading(false);
      });
    } catch (err) {
      console.error('❌ getSession exception:', err);
      setLoading(false);
    }

    // CRITICAL: This must remain synchronous
    let subscription = null;
    try {
      const result = supabase?.auth?.onAuthStateChange(
        authStateHandlers?.onChange
      );
      subscription = result?.data?.subscription;
    } catch (err) {
      console.error('❌ onAuthStateChange error:', err);
    }

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signUp = async (email, password, userData) => {
    try {
      const { data, error } = await authService?.signUp(email, password, userData)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signIn = async (email, password) => {
    try {
      const { data, error } = await authService?.signIn(email, password)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await authService?.signInWithGoogle()
      return { data, error }
    } catch (error) {
      return { error: { message: 'Google sign-in failed. Please try again.' } }
    }
  }

  const signInWithFacebook = async () => {
    try {
      const { data, error } = await authService?.signInWithFacebook()
      return { data, error }
    } catch (error) {
      return { error: { message: 'Facebook sign-in failed. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await authService?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await authService?.updateProfile(user?.id, updates)
      if (!error && data) {
        setUserProfile(data)
      }
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithFacebook,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
